import clsx from 'clsx';
import { EmblaCarouselType } from 'embla-carousel';
import AutoScroll from 'embla-carousel-auto-scroll';
import useEmblaCarousel, { UseEmblaCarouselType } from 'embla-carousel-react';
import {
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import styles from './EmblaCarousel.module.css';
import { EmblaDotButton, useEmblaDotButtons } from './EmblaCarouselPips';
import { useAutoScroll } from './useAutoScroll';

const MIN_SCALE = 0.8;

const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max);

const scaleForDistance = (distance: number): number => {
  const t = numberWithinRange(Math.abs(distance), 0, 1);
  const eased = (1 + Math.cos(t * Math.PI)) / 2;
  return MIN_SCALE + (1 - MIN_SCALE) * eased;
};

interface Props {
  emblaCarouselInfo?: UseEmblaCarouselType;
  /** The class name to put on the DIV that conatins carousel. Use it to set the CSS variables that power the carousel:
   *
   * --embla-carousel-gap
   * --embla-carousel-item-width
   *
   * It can also be used to apply spacing around the carousel or set its width
   */
  className?: string;
  items: ReactNode[];
  /** Whether to force the items to be height: 100%. This is needed if we are not relying on the carousel items to
   * define their height. e.g. if the Carousel is sitting within a container that is height 100% then this will need to
   * be set to true
   */
  stretchItemHeight?: boolean;
  /** New pip style/animation */
  pips2026?: boolean;
  /** Whether to prevent rendering of the pips. If the pips should be visible on some screen sizes and not others then
   * leave this as false/undefined and use media queries to show/hide the pips
   */
  hidePips?: boolean;
  /** Any children that should appear within the carousel's fixed container, e.g. overlays that should be position over
   * the carousel track
   */
  children?: ReactNode;
  /** By adding autoScroll the carousels items automatically start scrolling at 0.7 speed, speed can be set using scrollSpeed
   */
  autoScroll?: boolean;
  scrollSpeed?: number;
  /** By adding scaleCarousel, the selected item scales in and out as it becomes the selected item
   */
  scaleCarousel?: boolean;
}

/** Not so simple carousel, with some customisation */
const EmblaCarousel = (props: Props) => {
  const [emblaRef, emblaApi] =
    props.emblaCarouselInfo ??
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEmblaCarousel({ loop: false, duration: 10, inViewThreshold: 0.1 }, [
      AutoScroll({
        playOnInit: props.autoScroll ? true : false,
        speed: props.scrollSpeed ? props.scrollSpeed : 0.7,
      }),
    ]);
  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useEmblaDotButtons(emblaApi);
  const { autoScroll } = useAutoScroll(emblaApi);

  const containerRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const tweenNodes = useRef<HTMLElement[]>([]);

  useEffect(() => {
    if (props.autoScroll && !autoScroll?.isPlaying()) {
      autoScroll?.play();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.autoScroll, selectedIndex, scrollSnaps]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseUp = () => {
      if (props.autoScroll && autoScroll && !autoScroll.isPlaying()) {
        autoScroll.play();
      }
    };

    container.addEventListener('mouseup', handleMouseUp);
    return () => container.removeEventListener('mouseup', handleMouseUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.autoScroll, autoScroll]);

  const setTweenNodes = useCallback((emblaApi: EmblaCarouselType) => {
    tweenNodes.current = emblaApi.slideNodes();
  }, []);

  const tweenScale = useCallback((emblaApi: EmblaCarouselType) => {
    const rootNode = emblaApi.rootNode();
    if (!rootNode) return;

    const rootRect = rootNode.getBoundingClientRect();
    const viewportCenter = rootRect.left + rootRect.width / 2;

    tweenNodes.current.forEach((node) => {
      // Get the actual visual position of the slide (accounts for Embla's loop transforms)
      const slideRect = node.getBoundingClientRect();
      const slideCenter = slideRect.left + slideRect.width / 2;

      // Calculate distance from viewport center, normalized by slide width
      const distanceFromCenter = Math.abs(viewportCenter - slideCenter);
      const normalizedDistance = distanceFromCenter / slideRect.width;

      const scale = scaleForDistance(normalizedDistance);

      // Get Embla's existing translate transform and preserve it while adding scale
      const currentTransform = node.style.transform;
      const translateMatch = currentTransform.match(/translate3d\([^)]+\)/);
      const translate = translateMatch ? translateMatch[0] + ' ' : '';

      node.style.transform = `${translate}scale(${scale})`;
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    if (!props.scaleCarousel) {
      // Clear only the scale portion, preserve Embla's translate
      emblaApi.slideNodes().forEach((n) => {
        const currentTransform = n.style.transform;
        const translateMatch = currentTransform.match(/translate3d\([^)]+\)/);
        n.style.transform = translateMatch ? translateMatch[0] : '';
      });
      return;
    }

    setTweenNodes(emblaApi);
    tweenScale(emblaApi);

    emblaApi
      .on('reInit', setTweenNodes)
      .on('reInit', tweenScale)
      .on('scroll', tweenScale)
      .on('slidesInView', tweenScale)
      .on('slideFocus', tweenScale);

    return () => {
      emblaApi
        .off('reInit', setTweenNodes)
        .off('reInit', tweenScale)
        .off('scroll', tweenScale)
        .off('slidesInView', tweenScale)
        .off('slideFocus', tweenScale);
    };
  }, [emblaApi, setTweenNodes, tweenScale, props.scaleCarousel]);

  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, props.items.length]);

  return (
    <div className={props.className}>
      <div
        className={styles.embla}
        ref={(node) => {
          emblaRef(node);
          containerRef.current = node;
        }}
      >
        <div className={styles.emblaContainer}>
          {props.items.map((x, i) => (
            <div
              className={clsx(
                styles.emblaItem,
                props.stretchItemHeight && styles.emblaItemStretchHeight,
              )}
              key={i}
            >
              {x}
            </div>
          ))}
        </div>
      </div>
      {!props.hidePips && (
        <div
          className={clsx(
            props.pips2026 ? styles.pips2026 : '',
            styles.emblaButtonContainer,
          )}
        >
          {scrollSnaps.map((_, index) => (
            <EmblaDotButton
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={index === selectedIndex ? styles.selected : ''}
            />
          ))}
        </div>
      )}
      {props.children}
    </div>
  );
};

export default EmblaCarousel;
